/**
 * Explicit allowlist of (provider, platform) pairs whose cache read
 * path is ready for production traffic.
 *
 * **Why an allowlist (not a blocklist)?**
 * Cache reads apply to ALL authed axios traffic. Without a per-cohort
 * gate, providers whose cache branch is still a stub or carries known
 * regressions would silently break. The allowlist ensures only cohorts
 * that have been explicitly verified safe use the cache; everything else
 * falls through to the legacy Redux read — including future providers.
 *
 * **Platform awareness**
 * `AuthProvider.KINDE` covers both Kinde-web and Kinde-desktop, but
 * they have different readiness gates (Kinde-web needs the
 * `/api/kinde-session` endpoint to ship; Kinde-desktop has a known
 * zombie-escalation gap in the classification path). The check takes
 * `isTauri(): boolean` as an explicit argument so the caller controls
 * mock-ability in tests and there is no hidden platform dependency at
 * module scope.
 *
 * **Adding a new cohort**
 * Add a row here in the SAME PR as the safety fix that makes it ready.
 * Document the prerequisite next to the entry. The pair `[provider,
 * tauri]` is the key; set `tauri: false` for web-only readiness.
 *
 * Before adding a `tauri: true` row (`AUTH0_DESKTOP` etc.), each of the
 * following must be resolved — none of them are exercised by the current
 * web-only allowlist, so they've stayed latent gaps rather than shipped bugs:
 * (a) the `!isAuthenticated` gate on the Chronicle provider's clear-auth
 *     effect must actually be reachable under a slower cache round-trip, not
 *     just a fast Redux read;
 * (b) `agent-events-client`'s SSE loop treats a falsy token as a hard stop
 *     (silent return) rather than the backoff-and-continue it already does
 *     for a confirmed-offline desktop — a transient cache-not-ready falsy
 *     resolution would kill the subscription instead of retrying it;
 * (c) the Rust auth layer emits `token_refresh_needed` before `recover_auth()`
 *     has succeeded, so a non-forced FE read landing in that interval can
 *     return a token the backend has already rejected but the FE has not
 *     yet locally recognized as expired. Neither invalidating nor writing
 *     through the FE cache can bridge that interval safely — the FE has no
 *     visibility into the rotation until the Rust side has actually
 *     completed it. The plugin now emits `auth:session-refreshed` once the
 *     refresh has succeeded and been persisted — a notification only, with
 *     no payload, so no bearer token crosses the event bus. The remaining
 *     prerequisite is the FE listener: on each notification it re-reads the
 *     authoritative session via the `auth_session` command and publishes
 *     that response, epoch-guarding every effect and publishing any Redux,
 *     cache, or atom effect only after the read has been accepted. That
 *     command is reconciliation, not a pure read — it runs interrupted-
 *     logout recovery, and with no valid cached session may restore a
 *     snapshot or refresh again — so the listener must also coalesce
 *     concurrent notifications rather than reconciling once per event.
 *     (c-i) redux-only writers — closed BY THE SAME LISTENER that closes
 *           (c), not closed until that listener ships: `forceRefreshDesktopAuthToken`
 *           and the online-chunk-upload `setToken` call sites write the
 *           rotated token straight to Redux, bypassing the FE cache
 *           entirely, which could otherwise read as its own, separate
 *           staleness gap. It isn't a separate gap: every rotation path —
 *           these included — funnels through the plugin's single refresh
 *           worker in Rust, so every rotation reaches the same emit. That
 *           means the one FE listener required by (c) also closes this one,
 *           with no separate fix required — but, like (c), this gate stays
 *           open until that listener actually ships;
 * (d) `useChronicleStartRecording`'s token read sits on the recording-start
 *     critical path and uses the async helper (a potential cache round-trip
 *     / fetch) — worth reconsidering the sync helper there once this cohort
 *     is cache-enabled, to keep start latency where it is today.
 * (e) the session engine has no native account-switch fence: the global
 *     credential slot, the unowned task queue, and the retained note-default
 *     cache are none of them scoped to a particular account inside the Rust
 *     process, so a queued native task or the last-written credential can
 *     outlive an account switch entirely at that layer. FE-side write
 *     ordering — however carefully serialized — cannot fence work already
 *     queued past the JS boundary; a native account-switch fence in the
 *     session engine is required before any desktop row.
 *
 * | Provider          | Platform | Prerequisite / gate                               |
 * |-------------------|----------|---------------------------------------------------|
 * | AUTH0 (web)       | false    | Initial — consumer integration merged ✓           |
 * | AUTH0_DESKTOP     | true     | Rust invalid_grant signal (session_invalid reason) |
 * | KINDE (desktop)   | true     | Zombie-escalation path in readKindeDesktop         |
 * | KINDE (web)       | false    | readKindeWeb() + /api/kinde-session endpoint        |
 * | KEYCLOAK          | true     | Single-renewal-authority + Session E               |
 */

import type { AuthProvider } from '../contracts/authProvider';

interface CacheReadyEntry {
  provider: AuthProvider;
  /** true = Tauri desktop only; false = web only */
  tauri: boolean;
}

/**
 * Providers (by platform) for which the cache read path is enabled.
 *
 * Initial membership: `AUTH0` on web ONLY.
 * Each subsequent entry must land in the same PR as the fix that
 * makes that cohort safe — see the table above.
 */
const CACHE_READY_PROVIDERS: ReadonlyArray<CacheReadyEntry> = [
  { provider: 'AUTH0' as AuthProvider, tauri: false },
];

/**
 * Returns true when the given provider+platform combination is in the
 * cache-ready allowlist. Safe to call synchronously at request time.
 *
 * @param provider - The effective auth provider (cookie-first on web).
 * @param tauri    - Whether the current runtime is a Tauri desktop build.
 */
export function isCacheReadyProvider(
  provider: AuthProvider | null | undefined,
  tauri: boolean
): boolean {
  if (provider === null || provider === undefined) return false;
  return CACHE_READY_PROVIDERS.some(
    (entry) => entry.provider === provider && entry.tauri === tauri
  );
}
